import { useNarrativesCommunityInfinite } from '../hooks/useNarrativesCommunityInfinite';
import { NarrativeCardPopulated } from '@/components/NarrativeCard';
import { NARRATIVE_COMMUNITY_LIST_PAGE_SIZE } from '../utils';
import { Button } from '@simplywallst/ui-core';
import EyeSolid from '@simplywallst/ui-core/icons/EyeSolid';
import { NarrativesEmptyPanel } from '../../NarrativesEmptyPanel';
import IntersectionObserver from '@researchgate/react-intersection-observer';
import {
  FUTURE_NARRATIVE_ID,
  useNarrativeCommunityQueryParamCursors,
} from '../hooks/useNarrativeCommunityQueryParamCursors';
import { useNarrativeCommunityScroll } from '../hooks/useNarrativeCommunityScroll';
import { NarrativeCommunityListSkeleton } from './NarrativeCommunityListSkeleton';
import { useNarrativeCommunityGoToStart } from '../hooks/useNarrativeCommunityGoToStart';
import { useTracking } from 'react-tracking';
import { useCallback } from 'react';
import { cn } from '@simplywallst/ui-core/next';

export const NarrativeCommunityListMain = () => {
  const { trackEvent, Track } = useTracking({
    subject: 'listing',
  });
  const { updateNarrativeVisibility, initialBefore, initialAfter } =
    useNarrativeCommunityQueryParamCursors();
  const {
    data: narratives,
    isFetchingNextPage,
    isFetchingPreviousPage,
    hasNextPage,
    hasPreviousPage,
    fetchNextPage,
    fetchPreviousPage,
  } = useNarrativesCommunityInfinite({
    first: NARRATIVE_COMMUNITY_LIST_PAGE_SIZE,
    last: NARRATIVE_COMMUNITY_LIST_PAGE_SIZE,
    after: initialAfter,
    before: initialBefore,
  });

  const firstPage = narratives.pages[0].narratives;
  const isLoading = isFetchingNextPage || isFetchingPreviousPage;

  const handleFetchNextPage = useCallback(async () => {
    if (!hasNextPage || isLoading) return;

    await fetchNextPage();
    trackEvent({
      action: 'scroll_next_page',
    });
  }, [hasNextPage, isLoading, fetchNextPage, trackEvent]);

  useNarrativeCommunityGoToStart({
    hasFirstPage: firstPage.pageInfo.hasPreviousPage === false,
  });
  useNarrativeCommunityScroll(handleFetchNextPage);

  if (!narratives.pages.length || !firstPage.edges.length)
    return (
      <NarrativesEmptyPanel>
        There are no community narratives available.
        {/* TODO Add create narrative CTA button */}
      </NarrativesEmptyPanel>
    );

  return (
    <Track>
      <div className="grid gap-x2">
        {hasPreviousPage && (
          <div className="grid grid-cols-[minmax(0,300px)] place-content-center">
            <Button
              styleType="loudLarge"
              isLoading={isFetchingPreviousPage}
              disabled={isLoading}
              onClick={() => {
                fetchPreviousPage();
                trackEvent({
                  action: 'click_prev_page',
                });
              }}
              alignIcon="left"
            >
              <EyeSolid />
              Load previous page
            </Button>
          </div>
        )}
        <section
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x2"
          role="list"
        >
          {narratives.pages.map((page, pageIndex) =>
            page.narratives.edges.map(({ node: narrative }) => (
              <IntersectionObserver
                key={narrative.id}
                threshold={1}
                onChange={(entry) => {
                  if (!page.narratives.pageInfo.hasPreviousPage)
                    return updateNarrativeVisibility({
                      narrativeId: narrative.id,
                      isVisible: entry.isIntersecting,
                      after: null,
                      before: null,
                    });

                  const currentPageParam = narratives.pageParams[pageIndex] as {
                    after: string | null | undefined;
                    before: string | null | undefined;
                  };
                  updateNarrativeVisibility({
                    narrativeId: narrative.id,
                    isVisible: entry.isIntersecting,
                    after: currentPageParam.after,
                    before: currentPageParam.before,
                  });
                }}
              >
                <div role="listitem">
                  <NarrativeCardPopulated
                    showCompanyIcon
                    narrative={narrative}
                  />
                </div>
              </IntersectionObserver>
            ))
          )}
        </section>

        <IntersectionObserver
          threshold={0.5}
          onChange={(entry) => {
            // When user scrolled to the bottom of the page, in order to keep the query params
            // pointing to the last page, we manually set the after cursor to the last end cursor
            updateNarrativeVisibility({
              narrativeId: FUTURE_NARRATIVE_ID,
              isVisible: entry.isIntersecting,
              after: narratives.pages.at(-1)?.narratives.pageInfo.endCursor,
              before: null,
            });
          }}
        >
          <div className={cn(isFetchingNextPage ? 'block' : 'hidden')}>
            <NarrativeCommunityListSkeleton />
          </div>
        </IntersectionObserver>

        {hasNextPage && !isFetchingNextPage && (
          <div className="grid grid-cols-[minmax(0,300px)] place-content-center">
            <Button
              styleType="loudLarge"
              isLoading={isFetchingNextPage}
              disabled={isLoading}
              onClick={() => {
                fetchNextPage();
                trackEvent({
                  action: 'click_next_page',
                });
              }}
              alignIcon="left"
            >
              <EyeSolid />
              Load next page
            </Button>
          </div>
        )}
      </div>
    </Track>
  );
};
